import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { Dialog } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, THead, TBody, TR, TH, TD, EmptyRow } from '@/components/ui/table';
import { PageHeader, Loading, ErrorState, Pagination } from '@/components/ui/states';
import { useToast } from '@/components/ui/toast';
import { useWalletTransactions, useApproveTopup, useRejectTopup, useAdjustWalletBalance, type WalletTxFilters } from '@/api/hooks';
import { errorMessage } from '@/api/client';
import { formatUsdt, formatDateTime } from '@/lib/utils';

const STATUS_VARIANT = { pending: 'warning', verified: 'success', rejected: 'destructive' } as const;
const TYPE_LABEL = { topup: 'Top-up', order_payment: 'Order payment', refund: 'Refund', adjustment: 'Adjustment' } as const;

export function WalletPage() {
  const [filters, setFilters] = useState<WalletTxFilters>({ page: 1, pageSize: 20 });
  const txs = useWalletTransactions(filters);
  const approve = useApproveTopup();
  const reject = useRejectTopup();
  const { toast } = useToast();
  const [adjustOpen, setAdjustOpen] = useState(false);

  const set = (patch: Partial<WalletTxFilters>) => setFilters((f) => ({ ...f, page: 1, ...patch }));

  return (
    <div>
      <PageHeader
        title="Wallet"
        description="User wallet top-ups, order debits and manual adjustments."
        actions={<Button onClick={() => setAdjustOpen(true)}>Adjust balance</Button>}
      />

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Select
          value={filters.status ?? ''}
          onChange={(e) => set({ status: e.target.value || undefined })}
          placeholder="All statuses"
          options={[
            { value: 'pending', label: 'Pending review' },
            { value: 'verified', label: 'Verified' },
            { value: 'rejected', label: 'Rejected' },
          ]}
        />
        <Input
          placeholder="Telegram user ID"
          value={filters.telegramUserId ?? ''}
          onChange={(e) => set({ telegramUserId: e.target.value ? Number(e.target.value) : undefined })}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {txs.isLoading ? (
            <Loading />
          ) : txs.isError ? (
            <ErrorState error={txs.error} onRetry={() => txs.refetch()} />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Date</TH>
                  <TH>User</TH>
                  <TH>Type</TH>
                  <TH>Amount</TH>
                  <TH>Balance after</TH>
                  <TH>Reference</TH>
                  <TH>Status</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {txs.data && txs.data.items.length > 0 ? (
                  txs.data.items.map((tx) => (
                    <TR key={tx.id}>
                      <TD className="whitespace-nowrap text-muted-foreground">{formatDateTime(tx.createdAt)}</TD>
                      <TD className="text-muted-foreground">{tx.telegramUserId}</TD>
                      <TD>{TYPE_LABEL[tx.type]}</TD>
                      <TD className={tx.amountUsdt < 0 ? 'text-destructive' : 'text-success'}>
                        {tx.amountUsdt >= 0 ? '+' : ''}
                        {formatUsdt(tx.amountUsdt)}
                      </TD>
                      <TD>{tx.balanceAfterUsdt != null ? formatUsdt(tx.balanceAfterUsdt) : '—'}</TD>
                      <TD className="max-w-[160px] truncate font-mono text-xs" title={tx.reference}>
                        {tx.reference || '—'}
                      </TD>
                      <TD>
                        <Badge variant={STATUS_VARIANT[tx.status]}>{tx.status}</Badge>
                      </TD>
                      <TD className="text-right">
                        {tx.status === 'pending' ? (
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              loading={approve.isPending}
                              onClick={() =>
                                approve.mutate(
                                  { id: tx.id },
                                  {
                                    onSuccess: () => toast({ title: 'Top-up approved & credited', variant: 'success' }),
                                    onError: (e) => toast({ title: 'Approve failed', description: errorMessage(e), variant: 'error' }),
                                  },
                                )
                              }
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              loading={reject.isPending}
                              onClick={() =>
                                reject.mutate(
                                  { id: tx.id },
                                  {
                                    onSuccess: () => toast({ title: 'Top-up rejected', variant: 'success' }),
                                    onError: (e) => toast({ title: 'Reject failed', description: errorMessage(e), variant: 'error' }),
                                  },
                                )
                              }
                            >
                              Reject
                            </Button>
                          </div>
                        ) : null}
                      </TD>
                    </TR>
                  ))
                ) : (
                  <EmptyRow colSpan={8}>No wallet transactions match these filters.</EmptyRow>
                )}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {txs.data ? (
        <Pagination page={txs.data.page} totalPages={txs.data.totalPages} onPage={(p) => setFilters((f) => ({ ...f, page: p }))} />
      ) : null}

      <AdjustDialog open={adjustOpen} onClose={() => setAdjustOpen(false)} />
    </div>
  );
}

function AdjustDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const adjust = useAdjustWalletBalance();
  const { toast } = useToast();
  const [telegramUserId, setTelegramUserId] = useState('');
  const [amountUsdt, setAmountUsdt] = useState('');
  const [note, setNote] = useState('');

  function submit() {
    const userId = Number(telegramUserId);
    const amount = Number(amountUsdt);
    if (!Number.isInteger(userId) || !Number.isFinite(amount) || amount === 0 || !note.trim()) {
      toast({ title: 'Enter a user ID, non-zero amount, and a note', variant: 'error' });
      return;
    }
    adjust.mutate(
      { telegramUserId: userId, amountUsdt: amount, note: note.trim() },
      {
        onSuccess: (res) => {
          toast({ title: `Balance adjusted — new balance ${formatUsdt(res.newBalance)} USDT`, variant: 'success' });
          setTelegramUserId('');
          setAmountUsdt('');
          setNote('');
          onClose();
        },
        onError: (e) => toast({ title: 'Adjustment failed', description: errorMessage(e), variant: 'error' }),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Manually adjust a wallet balance"
      description="Use a negative amount to deduct. Always leave a note — it's kept in the ledger."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={adjust.isPending} onClick={submit}>
            Apply
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Telegram user ID">
          <Input value={telegramUserId} onChange={(e) => setTelegramUserId(e.target.value)} placeholder="123456789" />
        </Field>
        <Field label="Amount (USDT)" hint="Negative to deduct">
          <Input type="number" step="0.01" value={amountUsdt} onChange={(e) => setAmountUsdt(e.target.value)} placeholder="10.00" />
        </Field>
        <Field label="Note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for this adjustment" />
        </Field>
      </div>
    </Dialog>
  );
}
